'use client'

import { useCallback, useEffect, useRef, type RefObject } from 'react'

import {
  createProductTracker,
  type CtaPlacement,
  type ProductTracker,
} from './product-tracker'

interface UseProductTrackingOptions {
  /** Marker element after the last content section, before comments (§3.2). */
  contentEndRef: RefObject<HTMLElement | null>
  /** Visitor is the owner or a team member — events are tagged, not dropped (§3.7, §6.3). */
  selfView: boolean
  /** Start tracking only once the product page actually rendered. */
  enabled: boolean
}

export function useProductTracking(
  slug: string | undefined,
  { contentEndRef, selfView, enabled }: UseProductTrackingOptions,
) {
  const trackerRef = useRef<ProductTracker | null>(null)
  const selfViewRef = useRef(selfView)
  selfViewRef.current = selfView

  useEffect(() => {
    if (!enabled || !slug) return
    const tracker = createProductTracker(slug, () => contentEndRef.current)
    tracker.setSelfView(selfViewRef.current)
    trackerRef.current = tracker
    return () => {
      trackerRef.current = null
      tracker.destroy()
    }
    // contentEndRef is a stable ref object; only slug/enabled restart a session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, enabled])

  // Auth resolves after mount — propagate self-view to the live session (§6.3).
  useEffect(() => {
    trackerRef.current?.setSelfView(selfView)
  }, [selfView])

  return useCallback((placement: CtaPlacement) => {
    trackerRef.current?.trackCtaClick(placement)
  }, [])
}
